import React, { useState, useEffect } from 'react';
import { api } from '../api';
import OTPGate from './OTPGate';

/**
 * Device Test Tab - Florida 720.317 Compliance
 * Allows voters to test authentication before actual election
 */
const DeviceTestTab = ({ user }) => {
  const [testElection, setTestElection] = useState(null);
  const [voterToken, setVoterToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testVote, setTestVote] = useState('');
  const [castingBallot, setCastingBallot] = useState(false);
  const [ballotCast, setBallotCast] = useState(false);
  const [receiptHash, setReceiptHash] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadDeviceTest();
    }
  }, [user]);

  const loadDeviceTest = async () => {
    if (!user) return; // Don't load without user
    
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get('/api/vote/election/device-test');
      setTestElection(response.data.election);
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || 'Failed to load device test';
      setError(errorMsg);
      console.error('Device test load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthenticated = (token) => {
    setVoterToken(token);
  };

  const castTestBallot = async () => {
    if (!testVote) {
      setError('Please select an option');
      return;
    }

    setCastingBallot(true);
    setError('');

    try {
      const response = await api.post('/api/vote/ballot/cast', {
        voter_token: voterToken,
        election_id: testElection.id,
        selections: {
          'test_topic_1': testVote
        }
      });

      setBallotCast(true);
      setReceiptHash(response.data.receipt_hash);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to cast test ballot');
    } finally {
      setCastingBallot(false);
    }
  };

  const resetTest = () => {
    setVoterToken(null);
    setTestVote('');
    setBallotCast(false);
    setReceiptHash('');
    setError('');
  };

  // Wait for user to load
  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!testElection) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load device test</p>
        <button
          onClick={loadDeviceTest}
          className="mt-4 text-indigo-600 hover:text-indigo-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-8 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h1 className="text-3xl font-bold">Device Authentication Test</h1>
            </div>
            <p className="text-indigo-100">
              Test your device and email before the actual election
            </p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white/20 rounded-full p-4">
              <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Information Card */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Why Test Your Device?
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              This system tests the complete voting authentication process before the actual election, ensuring:
            </p>
            <ul className="text-sm text-blue-700 space-y-2">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Your email address is correct and receiving codes</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Your device (computer, phone, tablet) works with the system</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>You're familiar with the authentication process</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>You receive the ballot receipt (proof your vote was recorded)</span>
              </li>
            </ul>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-sm text-yellow-800 font-medium">
                ⚠️ Important: Test votes do NOT count in any real election
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {!voterToken ? (
        /* Step 1: Authentication */
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">
            Step 1: Test Authentication
          </h2>
          <OTPGate
            electionId={testElection.id}
            userEmail={user.email}
            onAuthenticated={handleAuthenticated}
            isTest={true}
          />
        </div>
      ) : !ballotCast ? (
        /* Step 2: Cast Test Ballot */
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Step 2: Cast Test Ballot
          </h2>
          
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {testElection.special_topics[0].title}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {testElection.special_topics[0].description}
              </p>
              
              <div className="space-y-3">
                {testElection.special_topics[0].options.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      testVote === option
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="test-vote"
                      value={option}
                      checked={testVote === option}
                      onChange={(e) => setTestVote(e.target.value)}
                      className="h-5 w-5 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="ml-3 text-gray-900 font-medium">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={castTestBallot}
              disabled={!testVote || castingBallot}
              className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {castingBallot ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Casting Test Ballot...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Cast Test Ballot
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        /* Step 3: Success & Receipt */
        <div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 mb-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-green-900 mb-2">
                ✓ Device Test Successful!
              </h2>
              <p className="text-green-700 mb-4">
                Your test ballot has been cast. Your device and email are working correctly.
              </p>
            </div>

            <div className="bg-white border border-green-200 rounded-lg p-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Your Ballot Receipt:</h3>
              <div className="bg-gray-50 p-3 rounded font-mono text-xs break-all">
                {receiptHash}
              </div>
              <p className="text-xs text-gray-600 mt-2">
                A copy has been emailed to you. This proves your ballot was recorded.
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">
              ✓ What You've Verified:
            </h3>
            <ul className="space-y-2 text-sm text-blue-700">
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Email delivery works (you received the authentication code)</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Authentication system works (code verified successfully)</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Ballot submission works (test vote was recorded)</span>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Receipt system works (you received your ballot hash)</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-lg p-6 text-center">
            <h3 className="text-xl font-bold mb-2">You're Ready for Election Day!</h3>
            <p className="text-green-100 mb-4">
              When the actual election opens, you'll follow the same process:
              request code → verify → cast ballot.
            </p>
            <button
              onClick={resetTest}
              className="bg-white text-green-700 px-6 py-2 rounded-lg font-medium hover:bg-green-50 transition-colors"
            >
              Test Again
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceTestTab;
